'use strict';

const khInitRuntime = window.khThemeRuntime || {};
const khInitMessage = (key, fallback) => window.khThemeRuntime?.messages?.[key] || khInitRuntime.messages?.[key] || fallback;
const khEscapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

let khFrontendLocaleRefreshController = null;
let khFrontendLocaleRefreshSequence = 0;
let khProfileLanguageSaveController = null;
let khProfileLanguageSaveSequence = 0;
let khContentLazyObserver = null;
const khActiveRestAjaxRequests = new Set();

function khIsAbortError(error) {
    return error?.name === 'AbortError'
        || error?.message === 'AbortError'
        || error === 'frontend-locale-change'
        || window.TeejitRestClient?.isAbortError?.(error);
}

function khAbortActiveRestAjaxRequests() {
    khActiveRestAjaxRequests.forEach((request) => {
        if (request && typeof request.abort === 'function') {
            request.abort();
        }
    });
    khActiveRestAjaxRequests.clear();
}

document.addEventListener('kh:frontendLocaleChanging', khAbortActiveRestAjaxRequests);

const getTeejitFixedTopOffset = () => {
    const candidates = Array.from(document.querySelectorAll('#wpadminbar, header, .t-nav-head'));
    const fixedBottom = candidates.reduce((bottom, element) => {
        if (!element) {
            return bottom;
        }

        const style = window.getComputedStyle(element);
        if (!['fixed', 'sticky'].includes(style.position)) {
            return bottom;
        }

        const rect = element.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top > 180) {
            return bottom;
        }

        return Math.max(bottom, rect.bottom);
    }, 0);

    const fallbackTopbar = document.querySelector('.t-nav-head');
    const fallbackRect = fallbackTopbar ? fallbackTopbar.getBoundingClientRect() : null;
    const fallbackHeight = fallbackTopbar
        ? Math.max(
            fallbackTopbar.offsetHeight,
            fallbackRect && fallbackRect.bottom > 0 && fallbackRect.top <= 180 ? fallbackRect.bottom : 0
        )
        : 0;

    return Math.ceil(Math.max(fixedBottom, fallbackHeight) + 32);
};

const scrollElementBelowTeejitTopbar = (element, behavior = 'smooth') => {
    if (!element) {
        return;
    }

    const accordionItem = typeof element.closest === 'function' ? element.closest('.accordion-item') : null;
    const anchor = element.matches && element.matches('summary.accordion-header')
        ? element
        : (
            (typeof element.querySelector === 'function' ? element.querySelector('summary.accordion-header') : null)
            || (accordionItem && accordionItem.querySelector('summary.accordion-header'))
            || element
        );
    const elementTop = anchor.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
        top: Math.max(elementTop - getTeejitFixedTopOffset(), 0),
        behavior,
    });
};

window.khGetFixedTopOffset = getTeejitFixedTopOffset;
window.khScrollElementBelowTopbar = scrollElementBelowTeejitTopbar;

/**
 * Wait for the DOM to load before initializing core UI functions.
 */
document.addEventListener('DOMContentLoaded', () => {
    // Initialize navigation toggle functionality
    initTeejit_Nav();
    initTeejit_MobileHeadNav();
    initTeejit_ProfileMenu();
    initTeejit_ProfileLanguageSwitcher();
    // Initialize loading circle while log in
    initTeejit_LoadingLogin();
    // Set up smooth scrolling for accordion headers
    initTeejit_scrollTo();
    // Set up accordion behavior (click and keyboard handling)
    initTeejit_accordion();
    initTeejit_ContentLazyItems();
    // Set up label filtering based on checkboxes
    initTeejit_LabelFilter();
    // Initialize Slick slider (if enabled)
    //initTeejit_slick();
    // Initialize password toggle for showing/hiding text
    initTeejit_PasswordToggleShow();
    // Initialize password toggle for showing/hiding text on register password one
    initTeejit_PasswordToggleShowRegOne();
    // Initialize password toggle for showing/hiding text on register password two
    initTeejit_PasswordToggleShowRegTwo();
    // Initialize live validation for the registration password field
    initRegisterPasswordValidation();
    // Initialize the profile editing overlay functionality
    init_Teejit_TeejitEditProfilData();
});

/**
 * Initialize the navigation toggle.
 * Attaches a click event to the navigation toggle button to show/hide the navigation pane.
 */
const initTeejit_Nav = () => {
    const mobileSidebarMediaQuery = window.matchMedia('(max-width: 991.98px)');

    const isMobileSidebar = () => mobileSidebarMediaQuery.matches;

    const setMobileSidebarOpen = (isOpen) => {
        const toggleButton = jQuery("#t-nav-toggle-left-menu");
        const iconSpan = toggleButton.find('.t-nav-head-element-icon');

        jQuery('body').toggleClass('kh-sidebar-mobile-open', isOpen);
        jQuery("#t-nav").css('display', '');

        toggleButton.attr('aria-expanded', isOpen ? 'true' : 'false');
        toggleButton.attr('aria-label', isOpen
            ? khInitMessage('closeLeftSidebar', 'Close left sidebar')
            : khInitMessage('openLeftSidebar', 'Open left sidebar')
        );
        iconSpan.text(isOpen ? 'left_panel_close' : 'left_panel_open');
    };

    const applySidebarState = (isCollapsed) => {
        const toggleButton = jQuery("#t-nav-toggle-left-menu");
        const iconSpan = toggleButton.find('.t-nav-head-element-icon');

        jQuery('body').toggleClass('kh-sidebar-collapsed', isCollapsed);
        jQuery("#t-nav").css('display', '');

        toggleButton.attr('aria-expanded', isCollapsed ? 'false' : 'true');
        toggleButton.attr('aria-label', isCollapsed
            ? khInitMessage('openLeftSidebar', 'Open left sidebar')
            : khInitMessage('closeLeftSidebar', 'Close left sidebar')
        );
        iconSpan.text(isCollapsed ? 'left_panel_open' : 'left_panel_close');
    };

    const applyResponsiveSidebarState = () => {
        if (isMobileSidebar()) {
            setMobileSidebarOpen(false);
            return;
        }

        setMobileSidebarOpen(false);
        applySidebarState(window.localStorage && window.localStorage.getItem('khSidebarCollapsed') === '1');
    };

    applyResponsiveSidebarState();

    jQuery(document).on("click", "#t-nav-toggle-left-menu", function (event) {
        event.stopPropagation();

        if (isMobileSidebar()) {
            setMobileSidebarOpen(!jQuery('body').hasClass('kh-sidebar-mobile-open'));
            return;
        }

        const isCollapsed = !jQuery('body').hasClass('kh-sidebar-collapsed');
        applySidebarState(isCollapsed);

        if (window.localStorage) {
            window.localStorage.setItem('khSidebarCollapsed', isCollapsed ? '1' : '0');
        }
    });
    // Keyboard-Support hinzufügen
    jQuery(document).on("keydown", "#t-nav-toggle-left-menu", function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            jQuery(this).click();
        }
    });

    jQuery(document).on('click', '#t-nav a', function () {
        if (isMobileSidebar()) {
            setMobileSidebarOpen(false);
        }
    });

    jQuery(document).on('click', function (event) {
        if (!isMobileSidebar() || !jQuery('body').hasClass('kh-sidebar-mobile-open')) {
            return;
        }

        const target = event.target;
        if (jQuery(target).closest('#t-nav, #t-nav-toggle-left-menu').length === 0) {
            setMobileSidebarOpen(false);
        }
    });

    jQuery(document).on('keydown', function (event) {
        if (event.key === 'Escape' && isMobileSidebar() && jQuery('body').hasClass('kh-sidebar-mobile-open')) {
            setMobileSidebarOpen(false);
            jQuery("#t-nav-toggle-left-menu").trigger('focus');
        }
    });

    const handleSidebarBreakpointChange = () => {
        applyResponsiveSidebarState();
    };

    if (typeof mobileSidebarMediaQuery.addEventListener === 'function') {
        mobileSidebarMediaQuery.addEventListener('change', handleSidebarBreakpointChange);
    } else if (typeof mobileSidebarMediaQuery.addListener === 'function') {
        mobileSidebarMediaQuery.addListener(handleSidebarBreakpointChange);
    }
};

const initTeejit_ProfileMenu = () => {
    const profileMenu = document.querySelector('.t-nav-profile-menu');

    if (!profileMenu) {
        return;
    }

    const toggleButton = profileMenu.querySelector('.t-nav-profile-toggle');
    const dropdown = profileMenu.querySelector('.t-nav-profile-dropdown');

    if (!toggleButton || !dropdown) {
        return;
    }

    if (profileMenu.dataset.khProfileMenuBound === '1') {
        return;
    }
    profileMenu.dataset.khProfileMenuBound = '1';

    const setOpen = (isOpen) => {
        profileMenu.classList.toggle('is-open', isOpen);
        toggleButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        dropdown.hidden = !isOpen;
    };

    toggleButton.addEventListener('click', (event) => {
        event.stopPropagation();
        const shouldOpen = dropdown.hidden;
        if (shouldOpen) {
            document.dispatchEvent(new CustomEvent('kh:topbar-menu-open', {
                detail: {
                    source: 'profile',
                },
            }));
        }
        setOpen(shouldOpen);
    });

    profileMenu.addEventListener('click', (event) => {
        if (event.target.closest('a, [data-kh-home-builder-toggle]')) {
            setOpen(false);
        }
    });

    document.addEventListener('click', (event) => {
        if (!dropdown.hidden && !profileMenu.contains(event.target)) {
            setOpen(false);
        }
    });

    document.addEventListener('kh:topbar-menu-open', (event) => {
        if (event.detail?.source !== 'profile') {
            setOpen(false);
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            setOpen(false);
            toggleButton.focus();
        }
    });
};

const initTeejit_ProfileLanguageSwitcher = () => {
    const switchers = Array.from(document.querySelectorAll('[data-kh-profile-language-switcher]'));

    if (!switchers.length) {
        return;
    }

    const runtime = window.khThemeRuntime || {};
    const accessToken = String(runtime.userAccessToken || '');
    const nonce = String(runtime.restNonce || '');

    const normalizeLocale = (value) => {
        const locale = String(value || '').trim();
        if (locale === 'de_DE' || locale === 'de-DE' || locale === 'de_de') {
            return 'de';
        }
        if (locale === 'en_US' || locale === 'en-US' || locale === 'en_us') {
            return 'en';
        }

        return locale;
    };

    const activeRuntimeLocale = normalizeLocale(runtime.frontendLocale || runtime.locale || '');

    const escapeSelectorValue = (value) => String(value).replace(/["\\]/g, '\\$&');

    const notify = (message, type = 'alert') => {
        if (!message) {
            return;
        }

        if (window.KHNotifications?.createNotifier) {
            const notifier = window.KHNotifications.createNotifier();
            if (type === 'success' && typeof notifier.success === 'function') {
                notifier.success(message);
                return;
            }
            if (typeof notifier.alert === 'function') {
                notifier.alert(message);
                return;
            }
        }

        if (window.khThemeRuntime?.debugAjax) {
            console.warn(message);
        }
    };

    const setSwitcherOpen = (switcher, isOpen) => {
        const toggle = switcher.querySelector('.t-nav-language-toggle');
        const dropdown = switcher.querySelector('.t-nav-language-dropdown');

        switcher.classList.toggle('is-open', isOpen);
        if (switcher.matches('details')) {
            switcher.open = isOpen;
        }
        if (toggle) {
            toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }
        if (dropdown) {
            dropdown.hidden = !isOpen;
        }
    };

    const syncSwitcherLocale = (switcher, locale) => {
        const normalizedLocale = normalizeLocale(locale);
        const option = normalizedLocale
            ? switcher.querySelector('[data-kh-profile-language-option][data-locale="' + escapeSelectorValue(normalizedLocale) + '"]')
            : null;

        if (!option) {
            return;
        }

        const optionLabel = option.querySelector('.t-nav-language-option-label, span:last-child')?.textContent?.trim() || '';
        const optionFlagCountry = String(option.dataset.flagCountry || 'xx').trim() || 'xx';
        const currentFlag = switcher.querySelector('[data-kh-language-flag]');
        const currentLabel = switcher.querySelector('.t-nav-language-current');

        switcher.dataset.currentLocale = normalizedLocale;
        switcher.querySelectorAll('[data-kh-profile-language-option]').forEach((item) => {
            const isActive = normalizeLocale(item.dataset.locale || '') === normalizedLocale;
            item.classList.toggle('is-active', isActive);
            if (isActive) {
                item.setAttribute('aria-current', 'true');
            } else {
                item.removeAttribute('aria-current');
            }
        });

        if (currentFlag) {
            Array.from(currentFlag.classList).forEach((className) => {
                if (className.indexOf('kh-language-flag-') === 0) {
                    currentFlag.classList.remove(className);
                }
            });
            currentFlag.classList.add('kh-language-flag-' + optionFlagCountry);
        }
        if (currentLabel && optionLabel) {
            currentLabel.textContent = optionLabel;
        }
    };

    const setSavingState = (isSaving) => {
        document.documentElement.classList.toggle('kh-profile-language-saving', isSaving);
        switchers.forEach((item) => {
            item.classList.toggle('is-saving', isSaving);
            item.setAttribute('aria-busy', isSaving ? 'true' : 'false');
            item.querySelectorAll('button').forEach((button) => {
                button.disabled = isSaving;
            });
            setSwitcherOpen(item, false);
        });
    };

    const closeOtherTopbarMenus = () => {
        document.dispatchEvent(new CustomEvent('kh:topbar-menu-open', {
            detail: {
                source: 'language',
            },
        }));
    };

    const saveLocale = async (switcher, locale) => {
        const endpoint = String(switcher.dataset.endpoint || '').trim();
        const currentLocale = String(switcher.dataset.currentLocale || '').trim();
        let localeSaved = false;

        if (!endpoint || !locale || locale === currentLocale) {
            setSwitcherOpen(switcher, false);
            return;
        }

        if (khProfileLanguageSaveController && typeof khProfileLanguageSaveController.abort === 'function') {
            khProfileLanguageSaveController.abort('frontend-locale-change');
        }

        const saveSequence = ++khProfileLanguageSaveSequence;
        const saveController = typeof window.AbortController === 'function'
            ? new window.AbortController()
            : null;
        khProfileLanguageSaveController = saveController;
        setSavingState(true);

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    ...(nonce ? {'X-WP-Nonce': nonce} : {}),
                    ...(accessToken ? {Authorization: 'Bearer ' + accessToken} : {}),
                    'X-Frontend-Locale': locale,
                },
                body: JSON.stringify({frontend_locale: locale}),
                ...(saveController ? {signal: saveController.signal} : {}),
            });
            const payload = await response.json().catch(() => ({}));

            if (saveSequence !== khProfileLanguageSaveSequence) {
                return;
            }

            if (!response.ok || payload?.success === false) {
                const saveError = new Error('language_update_failed');
                saveError.userMessage = payload?.message || '';
                throw saveError;
            }

            localeSaved = true;
            const nextLocale = setKhFrontendLocale(locale);
            switchers.forEach((item) => syncSwitcherLocale(item, nextLocale));

            await khSoftRefreshCurrentFrontendView(nextLocale);
        } catch (error) {
            if (khIsAbortError(error)) {
                return;
            }
            if (window.khThemeRuntime?.debugAjax) {
                console.warn('Soft language refresh failed, falling back to page reload.', error);
            }
            if (localeSaved) {
                window.location.reload();
                return;
            }
            notify(
                error?.userMessage || window.khThemeRuntime?.messages?.profileLanguageUpdateFailed || 'Language could not be changed.',
                'alert'
            );
        } finally {
            if (saveSequence === khProfileLanguageSaveSequence) {
                khProfileLanguageSaveController = null;
                setSavingState(false);
            }
        }
    };

    switchers.forEach((switcher) => {
        if (activeRuntimeLocale) {
            syncSwitcherLocale(switcher, activeRuntimeLocale);
        }

        if (switcher.dataset.khLanguageSwitcherBound === '1') {
            return;
        }
        switcher.dataset.khLanguageSwitcherBound = '1';

        const toggle = switcher.querySelector('.t-nav-language-toggle');
        const dropdown = switcher.querySelector('.t-nav-language-dropdown');

        if (toggle && dropdown) {
            toggle.addEventListener('click', (event) => {
                event.stopPropagation();
                const shouldOpen = dropdown.hidden;
                if (shouldOpen) {
                    closeOtherTopbarMenus();
                }
                setSwitcherOpen(switcher, shouldOpen);
            });
        }

        switcher.addEventListener('click', (event) => {
            const option = event.target.closest('[data-kh-profile-language-option]');
            if (!option || !switcher.contains(option)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            saveLocale(switcher, normalizeLocale(option.dataset.locale || ''));
        });
    });

    document.addEventListener('click', (event) => {
        switchers.forEach((switcher) => {
            if (!switcher.contains(event.target)) {
                setSwitcherOpen(switcher, false);
            }
        });
    });

    document.addEventListener('kh:topbar-menu-open', (event) => {
        if (event.detail?.source !== 'language') {
            switchers.forEach((switcher) => setSwitcherOpen(switcher, false));
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') {
            return;
        }

        switchers.forEach((switcher) => {
            const wasOpen = switcher.open || switcher.classList.contains('is-open');
            setSwitcherOpen(switcher, false);
            if (wasOpen) {
                switcher.querySelector('.t-nav-language-toggle, .t-nav-head-mobile-language-toggle')?.focus();
            }
        });
    });
};

const initTeejit_MobileHeadNav = () => {
    const wrapper = document.querySelector('.t-nav-head-wrapper');
    const toggleButton = document.querySelector('.t-nav-head-mobile-toggle');
    const mobileMenu = document.getElementById('t-nav-head-mobile-menu');

    if (!wrapper || !toggleButton || !mobileMenu) {
        return;
    }

    if (wrapper.dataset.khMobileHeadNavBound === '1') {
        return;
    }
    wrapper.dataset.khMobileHeadNavBound = '1';

    const icon = toggleButton.querySelector('.t-nav-head-element-icon');
    const openLabel = toggleButton.dataset.openLabel || 'Open navigation menu';
    const closeLabel = toggleButton.dataset.closeLabel || 'Close navigation menu';

    const updateMenuHeight = () => {
        if (mobileMenu.hidden) {
            return;
        }

        const viewport = window.visualViewport;
        const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
        const availableHeight = Math.max(0, viewportBottom - mobileMenu.getBoundingClientRect().top - 16);
        mobileMenu.style.setProperty('--kh-mobile-menu-max-height', availableHeight + 'px');
    };

    const setOpen = (isOpen) => {
        wrapper.classList.toggle('is-mobile-menu-open', isOpen);
        toggleButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        toggleButton.setAttribute('aria-label', isOpen ? closeLabel : openLabel);
        mobileMenu.hidden = !isOpen;
        if (isOpen) {
            mobileMenu.scrollTop = 0;
            updateMenuHeight();
        } else {
            mobileMenu.querySelectorAll('.t-nav-head-mobile-language[open]').forEach((switcher) => {
                switcher.open = false;
            });
        }

        if (icon) {
            icon.textContent = isOpen ? 'close' : 'menu';
        }
    };

    toggleButton.addEventListener('click', (event) => {
        event.stopPropagation();
        setOpen(mobileMenu.hidden);
    });

    mobileMenu.addEventListener('click', (event) => {
        if (event.target.closest('a, [data-kh-home-builder-toggle]')) {
            setOpen(false);
        }
    });

    document.addEventListener('click', (event) => {
        if (!mobileMenu.hidden && !wrapper.contains(event.target)) {
            setOpen(false);
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !mobileMenu.hidden) {
            setOpen(false);
            toggleButton.focus();
        }
    });

    const desktopMediaQuery = window.matchMedia('(min-width: 901px)');
    const closeOnDesktop = () => {
        if (desktopMediaQuery.matches) {
            setOpen(false);
        }
    };

    if (typeof desktopMediaQuery.addEventListener === 'function') {
        desktopMediaQuery.addEventListener('change', closeOnDesktop);
    } else if (typeof desktopMediaQuery.addListener === 'function') {
        desktopMediaQuery.addListener(closeOnDesktop);
    }

    window.addEventListener('resize', updateMenuHeight);
    window.addEventListener('scroll', updateMenuHeight, {passive: true});
    window.visualViewport?.addEventListener('resize', updateMenuHeight);
    window.visualViewport?.addEventListener('scroll', updateMenuHeight);
};

/**
 * Initialize the loading circle while loging in.
 * Attaches a click event to the login submit button to show/hide the teejit loading logo.
 */
const initTeejit_LoadingLogin = () => {
    jQuery(document).on("click", "#teejit-login-submit", function (e) {
        const email = jQuery("#user_email").val().trim();
        const password = jQuery("#user_password").val().trim();

        if (email === "" || password === "") {
            e.preventDefault();
            return false;
        }

        //jQuery("#t-login-form-loading").attr('style', 'display: flex !important');
    });
};

/**
 * Initialize accordion functionality.
 * Adds click and keyboard event listeners to elements with the "accordion-buttons" class,
 * enabling toggling of corresponding content sections and keyboard navigation.
 */
const initTeejit_accordion = () => {
    const buttons = Array.from(document.querySelectorAll('.accordion-buttons'))
        .filter((button) => button.dataset.accordionInitialized !== '1');
    buttons.forEach(button => {
        button.dataset.accordionInitialized = '1';
        // On click: toggle the aria-expanded state and associated content visibility.
        button.addEventListener('click', () => {
            const isExpanded = button.getAttribute('aria-expanded') === 'true';
            const contentId = button.getAttribute('aria-controls');
            const content = document.getElementById(contentId);
            // Toggle expanded attribute and hide/show content accordingly
            button.setAttribute('aria-expanded', !isExpanded);
            content.hidden = isExpanded;
        });
        // On keydown: handle keyboard navigation using ArrowUp, ArrowDown, Home, and End keys.
        button.addEventListener('keydown', (e) => {
            // Use the cached buttons array for navigation, not a new query
            const index = buttons.indexOf(button);
            switch (e.key) {
                case 'ArrowUp':
                    e.preventDefault();
                    if (index > 0) {
                        buttons[index - 1].focus();
                    }
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    if (index < buttons.length - 1) {
                        buttons[index + 1].focus();
                    }
                    break;
                case 'Home':
                    e.preventDefault();
                    buttons[0].focus();
                    break;
                case 'End':
                    e.preventDefault();
                    buttons[buttons.length - 1].focus();
                    break;
            }
        });
    });
};

/**
 * Initialize smooth scrolling for accordion headers.
 * When a summary element with class "accordion-header" is clicked, the window scrolls smoothly to the
 * associated content element, offset by the header height.
 */
const initTeejit_scrollTo = () => {
    jQuery(document)
        .off("click.khAccordionScroll", "summary.accordion-header")
        .on("click.khAccordionScroll", "summary.accordion-header", function () {
            const summary = this;
            const accordionItem = summary.closest('.accordion-item');
            const details = summary.closest('details');

            window.setTimeout(() => {
                if (details && details.open) {
                    scrollElementBelowTeejitTopbar(summary || accordionItem);
                }
            }, 180);
        });
};

/**
 * Initialize label filtering.
 * When a label in the filter section is clicked, this function toggles the display of related elements
 * based on the associated checkbox's state.
 */
const initTeejit_LabelFilter = () => {
    jQuery('.t-filter-main-element label').on('click', function () {
        // Obtain the checkbox associated with this label
        const checkbox = jQuery(this).siblings('input[type="checkbox"]');
        // Retrieve the checkbox name and split it to extract variable parts
        const checkboxName = checkbox.attr('name');
        const nameParts = checkboxName ? checkboxName.split('_') : [];
        if (nameParts.length === 4) {
            const variable = nameParts[2];
            const zahl = nameParts[3];
            // Select target elements that have the matching data attribute
            const targetElements = jQuery(`.t-button-directory[data-${variable}="${zahl}"]`);
            // Delay is set to ensure the checkbox state has updated before toggling display
            setTimeout(function () {
                if (checkbox.is(':checked')) {
                    targetElements.css('display', 'flex');
                } else {
                    targetElements.css('display', 'none');
                }
            }, 0);
        }
    });
};

/**
 * Initialize the profile data editing overlay.
 * When an element with the "edit" class is clicked, an overlay with a simple form is created for editing.
 */
const init_Teejit_TeejitEditProfilData = () => {
    jQuery(".edit").on("click", function () {
        // Get the closest parent row identifier
        const row = this.closest('.t-table-row');
        const id = row ? row.id : null;
        // Create an overlay to cover the current content
        var overlay = $('<div>').css({
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'grey',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        });
        // Define the container for the form with white background and padding
        var formContainer = $('<div>').css({
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '5px'
        });
        // Create a form element
        var form = $('<form>');
        // Add three text input fields to the form
        for (var i = 1; i <= 3; i++) {
            form.append($('<input>').attr({
                type: 'text',
                placeholder: khInitMessage('input', 'Input') + ' ' + i
            }).css({
                display: 'block',
                margin: '10px 0'
            }));
        }
        // Create a submit button
        var submitButton = $('<button>').attr('type', 'submit').text(khInitMessage('submit', 'Submit')).css({
            display: 'block',
            margin: '10px 0'
        });
        // Create a close button that removes the overlay on click
        var closeButton = $('<button>').text(khInitMessage('close', 'Close')).css({
            display: 'block',
            margin: '10px 0'
        });
        closeButton.on('click', function () {
            overlay.remove();
        });
        // Append the buttons to the form and the form to the container
        form.append(submitButton, closeButton);
        formContainer.append(form);
        // Append the form container to the overlay and insert the overlay into the page
        overlay.append(formContainer);
        $('#t-content-wrapper').append(overlay);
    });
};

const initRegisterPasswordValidation = () => {
    const forms = [
        {
            form: '#t-registerform',
            password: '#user_password_one',
            confirm: '#user_password_two',
            rules: '#register-password-rules',
            feedback: '#register-password-feedback',
            submit: '#teejit-register-submit',
        },
        {
            form: '#t-lostpassword-form',
            password: '#user_password_one',
            confirm: '#user_password_two',
            rules: '#lost-password-rules',
            feedback: '#lost-password-feedback',
            submit: '#teejit-login-submit',
        },
        {
            form: '#t-resetpassword-form',
            password: '#user_password_one',
            confirm: '#user_password_two',
            rules: '#reset-password-rules',
            feedback: '#reset-password-feedback',
            submit: '#teejit-reset-submit',
        },
        {
            form: 'form[data-profile-ajax="password"]',
            password: '#password-1',
            confirm: '#password-2',
            rules: '#profile-password-rules',
            feedback: '#profile-password-feedback',
            submit: '#teejit-profile-password-submit',
        },
    ];

    forms.forEach((config) => {
        const form = document.querySelector(config.form);
        const passwordField = document.querySelector(config.password);
        const confirmField = document.querySelector(config.confirm);
        const rulesContainer = document.querySelector(config.rules);
        const feedback = document.querySelector(config.feedback);
        const submitButton = document.querySelector(config.submit);

        if (!form || !passwordField || !confirmField || !rulesContainer || !submitButton) {
            return;
        }

        const allowedCharsPattern = /^[A-Za-z0-9#+*$&§!?_\-()\[\]=/@]+$/u;
        const specialCharsPattern = /[#\+\*\$&§!\?_\-\(\)\[\]=\/@]/u;
        const invalidMessage = rulesContainer.dataset.invalidMessage || 'Please meet all password requirements.';
        const mismatchMessage = rulesContainer.dataset.mismatchMessage || 'Passwords do not match.';
        const allowedCharsMessage = rulesContainer.dataset.allowedCharsMessage || 'Allowed special characters are only: #+*$&§!?_-()[]=/@';
        const unmetRuleMessages = {
            'letter-case': rulesContainer.dataset.ruleLetterCaseMessage || 'Uppercase and lowercase letters are required.',
            'number': rulesContainer.dataset.ruleNumberMessage || 'At least 1 number is required.',
            'special': rulesContainer.dataset.ruleSpecialMessage || 'At least 1 allowed special character is required.',
            'length': rulesContainer.dataset.ruleLengthMessage || 'At least 10 characters are required.',
        };

        const detailsList = feedback ? feedback.querySelector('[data-role="details"]') : null;
        const allowedCharsNote = rulesContainer.querySelector('[data-role="allowed-chars-note"]');
        const requiredFieldControls = Array.from(form.querySelectorAll('[data-required-field-error]'));
        let hasSubmitted = !feedback?.hidden;

        const getState = (password) => {
            const hasUppercase = /[A-Z]/.test(password);
            const hasLowercase = /[a-z]/.test(password);

            return {
                'letter-case': hasUppercase && hasLowercase,
                'number': /\d/.test(password),
                'special': specialCharsPattern.test(password),
                'length': password.length >= 10,
                allowedCharactersOnly: password.length > 0 && allowedCharsPattern.test(password),
            };
        };

        const updateRuleState = (ruleName, isValid, hasInput) => {
            const ruleElement = rulesContainer.querySelector(`[data-rule="${ruleName}"]`);

            if (!ruleElement) {
                return;
            }

            ruleElement.classList.toggle('is-valid', hasInput && isValid);
            ruleElement.classList.toggle('is-invalid', hasInput && !isValid);
            ruleElement.classList.toggle('is-pending', !hasInput);
        };

        const renderFeedback = (messages) => {
            if (!feedback || !detailsList) {
                return;
            }

            if (!messages.length) {
                feedback.hidden = true;
                detailsList.innerHTML = '';
                return;
            }

            detailsList.innerHTML = messages.map((message) => `<li>${message}</li>`).join('');
            feedback.hidden = false;
        };

        const collectValidationMessages = (passwordIsValid, confirmMatches, state) => {
            const messages = [];

            if (!passwordIsValid) {
                Object.entries(unmetRuleMessages).forEach(([ruleName, message]) => {
                    if (!state[ruleName]) {
                        messages.push(message);
                    }
                });

                if (passwordField.value !== '' && !state.allowedCharactersOnly) {
                    messages.push(allowedCharsMessage);
                }
            }

            if (!confirmMatches) {
                messages.push(mismatchMessage);
            }

            return messages;
        };

        const syncSubmitState = () => {
            submitButton.disabled = false;
            submitButton.setAttribute('aria-disabled', 'false');
        };

        const syncRequiredFieldError = (field, shouldShow) => {
            const errorId = field.dataset.requiredFieldError;
            const error = errorId ? document.getElementById(errorId) : null;
            const fieldIsEmpty = (field.type === 'checkbox' || field.type === 'radio')
                ? !field.checked
                : String(field.value || '').trim() === '';
            const isInvalid = Boolean(field.required && fieldIsEmpty);

            field.classList.toggle('is-invalid', shouldShow && isInvalid);
            field.setAttribute('aria-invalid', shouldShow && isInvalid ? 'true' : 'false');

            if (error) {
                error.hidden = !(shouldShow && isInvalid);
            }

            return isInvalid;
        };

        const syncRequiredFieldErrors = (shouldShow) => {
            let firstInvalidField = null;

            requiredFieldControls.forEach((field) => {
                const isInvalid = syncRequiredFieldError(field, shouldShow);
                if (isInvalid && !firstInvalidField) {
                    firstInvalidField = field;
                }
            });

            return firstInvalidField;
        };

        const syncValidation = () => {
            const password = passwordField.value;
            const confirmPassword = confirmField.value;
            const hasPasswordInput = password.length > 0;
            const state = getState(password);

            Object.entries(state).forEach(([ruleName, isValid]) => {
                if (ruleName === 'allowedCharactersOnly') {
                    return;
                }

                updateRuleState(ruleName, isValid, hasPasswordInput);
            });

            if (allowedCharsNote) {
                allowedCharsNote.hidden = !hasPasswordInput || state.allowedCharactersOnly;
            }

            const passwordIsValid = hasPasswordInput &&
                state['letter-case'] &&
                state['number'] &&
                state['special'] &&
                state['length'] &&
                state.allowedCharactersOnly;

            const confirmMatches = confirmPassword === '' || password === confirmPassword;

            passwordField.setCustomValidity(password === '' || passwordIsValid ? '' : invalidMessage);
            confirmField.setCustomValidity(confirmMatches ? '' : mismatchMessage);

            if (hasSubmitted) {
                const messages = collectValidationMessages(passwordIsValid, confirmMatches, state);
                renderFeedback(messages);
                syncRequiredFieldErrors(true);
            }

            syncSubmitState();

            return { passwordIsValid, confirmMatches, state };
        };

        passwordField.addEventListener('input', syncValidation);
        passwordField.addEventListener('blur', syncValidation);
        confirmField.addEventListener('input', syncValidation);
        confirmField.addEventListener('blur', syncValidation);
        form.addEventListener('input', syncSubmitState);
        form.addEventListener('change', syncSubmitState);
        requiredFieldControls.forEach((field) => {
            field.addEventListener('input', () => syncRequiredFieldError(field, hasSubmitted));
            field.addEventListener('change', () => syncRequiredFieldError(field, hasSubmitted));
        });

        submitButton.addEventListener('click', (event) => {
            hasSubmitted = true;
            const { passwordIsValid, confirmMatches, state } = syncValidation();
            const messages = collectValidationMessages(passwordIsValid, confirmMatches, state);
            const firstInvalidRequiredField = syncRequiredFieldErrors(true);

            if (!messages.length && !firstInvalidRequiredField) {
                renderFeedback([]);
                return;
            }

            event.preventDefault();
            renderFeedback(messages);

            if (!passwordIsValid) {
                passwordField.focus();
                return;
            }

            if (firstInvalidRequiredField) {
                firstInvalidRequiredField.focus();
                return;
            }

            confirmField.focus();
        });

        form.addEventListener('submit', (event) => {
            const { passwordIsValid, confirmMatches, state } = syncValidation();
            const messages = collectValidationMessages(passwordIsValid, confirmMatches, state);
            const firstInvalidRequiredField = syncRequiredFieldErrors(true);

            if (!messages.length && !firstInvalidRequiredField) {
                renderFeedback([]);
                return;
            }

            event.preventDefault();
            hasSubmitted = true;
            renderFeedback(messages);

            if (!passwordIsValid) {
                passwordField.focus();
                return;
            }

            if (firstInvalidRequiredField) {
                firstInvalidRequiredField.focus();
                return;
            }

            confirmField.focus();
        });

        syncValidation();
        syncSubmitState();
    });
};

/**
 * Initialize the password visibility toggle.
 * Changes the password input field type between "password" and "text" and updates the toggle icon accordingly.
 */
const initTeejit_PasswordToggleShow = () => {
    const $toggle = jQuery('#togglePassword');
    const $passwordField = jQuery('#user_password');
    const $toggleSpan = $toggle.find('span');
    $toggle.click(function () {
        const fieldType = $passwordField.attr('type');
        if (fieldType === 'text') {
            // Hide the password by changing type to "password" and update icon text
            $passwordField.attr('type', 'password');
            $toggleSpan.text('visibility');
        } else {
            // Show the password by changing type to "text" and update icon text
            $passwordField.attr('type', 'text');
            $toggleSpan.text('visibility_off');
        }
    });
};
const initTeejit_PasswordToggleShowRegOne = () => {
    const $toggle = jQuery('#togglePasswordOne');
    const $passwordField = jQuery('#user_password_one');
    const $toggleSpan = $toggle.find('span');
    $toggle.click(function () {
        const fieldType = $passwordField.attr('type');
        if (fieldType === 'text') {
            // Hide the password by changing type to "password" and update icon text
            $passwordField.attr('type', 'password');
            $toggleSpan.text('visibility');
        } else {
            // Show the password by changing type to "text" and update icon text
            $passwordField.attr('type', 'text');
            $toggleSpan.text('visibility_off');
        }
    });
};
const initTeejit_PasswordToggleShowRegTwo = () => {
    const $toggle = jQuery('#togglePasswordTwo');
    const $passwordField = jQuery('#user_password_two');
    const $toggleSpan = $toggle.find('span');
    $toggle.click(function () {
        const fieldType = $passwordField.attr('type');
        if (fieldType === 'text') {
            // Hide the password by changing type to "password" and update icon text
            $passwordField.attr('type', 'password');
            $toggleSpan.text('visibility');
        } else {
            // Show the password by changing type to "text" and update icon text
            $passwordField.attr('type', 'text');
            $toggleSpan.text('visibility_off');
        }
    });
};
/**
 * Initialize the Slick slider.
 * The initialization code for the slider has been commented out.
 * Uncomment the relevant block to enable the slider functionality.
 */
function initTeejit_slick() {
    jQuery(function () {
        jQuery('.slick-off').slick({
            infinite: true,
            slidesToShow: 3,
            slidesToScroll: 3
        });
        jQuery('.t-row-single-off').slick({
            slidesToShow: 3,
            slidesToScroll: 1,
            dots: true
        });
    });
}

/**
 * Executes a function when the DOM is ready.
 * If the document is already loaded, the function runs immediately;
 * otherwise, it is executed once the document is ready.
 *
 * @param {Function} fn - The function to execute when the DOM is ready.
 */
function executeWhenReady(fn) {
    if (document.readyState !== 'loading') {
        fn();
    } else {
        jQuery(document).ready(fn);
    }
}

function executeWhenVisible(selector, fn, rootMargin = '220px') {
    executeWhenReady(() => {
        const target = document.querySelector(selector);
        if (!target || !('IntersectionObserver' in window)) {
            fn();
            return;
        }

        let executed = false;
        const run = () => {
            if (executed) {
                return;
            }
            executed = true;
            fn();
        };

        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                observer.disconnect();
                run();
            }
        }, { rootMargin });

        observer.observe(target);
    });
}

const teamContentPagerControllers = new WeakMap();

function getTeamContentPagerForContainer(container) {
    if (!container) {
        return null;
    }

    const pager = container.matches?.('[data-team-content-pager]')
        ? container
        : container.querySelector('[data-team-content-pager]');

    return pager ? teamContentPagerControllers.get(pager) || null : null;
}

function ensureTeamContentPagerLoaded(container) {
    const controller = getTeamContentPagerForContainer(container);
    if (!controller || !controller.hasMore()) {
        return null;
    }

    return controller.loadAll();
}

function initLazyTeamContentPager(container, teamId, accessToken, fallbackLimit = 10) {
    if (!container) {
        return null;
    }

    const pager = container.querySelector('[data-team-content-pager]');
    if (!pager || teamContentPagerControllers.has(pager)) {
        return pager ? teamContentPagerControllers.get(pager) : null;
    }

    const list = pager.querySelector('[data-team-content-list]');
    const loader = pager.querySelector('[data-team-content-loader]');
    const resolvedTeamId = pager.getAttribute('data-team-id') || teamId;
    const limit = Math.max(1, Math.min(parseInt(pager.getAttribute('data-limit') || fallbackLimit, 10) || fallbackLimit, 50));
    let nextOffset = Math.max(0, parseInt(pager.getAttribute('data-next-offset') || '0', 10) || 0);
    let pendingPageRequest = null;
    let loadAllPromise = null;

    if (!list || !loader || !resolvedTeamId) {
        return null;
    }

    const hasMore = () => pager.getAttribute('data-has-more') === '1';
    const setHasMore = (value) => {
        pager.setAttribute('data-has-more', value ? '1' : '0');
        loader.hidden = !value;
    };
    const appendCards = (html) => {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html || '';
        const meta = wrapper.querySelector('[data-team-content-page-meta]');
        const existingIds = new Set(Array.from(list.querySelectorAll('.t-button-directory[data-load-id]'))
            .map((card) => card.getAttribute('data-load-id')));
        const cards = Array.from(wrapper.querySelectorAll('.t-button-directory'));
        const fragment = document.createDocumentFragment();
        let appended = 0;

        cards.forEach((card) => {
            const id = card.getAttribute('data-load-id');
            if (id && existingIds.has(id)) {
                return;
            }
            if (id) {
                existingIds.add(id);
            }
            fragment.appendChild(card);
            appended++;
        });

        if (appended) {
            list.appendChild(fragment);
        }

        const metaNextOffset = parseInt(meta?.getAttribute('data-next-offset') || '', 10);
        nextOffset = Number.isInteger(metaNextOffset) && metaNextOffset >= 0
            ? metaNextOffset
            : nextOffset + appended;
        pager.setAttribute('data-next-offset', String(nextOffset));
        if (meta) {
            const metaTotal = meta.getAttribute('data-total');
            if (metaTotal !== null) {
                pager.setAttribute('data-total', metaTotal);
            }
            const metaTotalEstimate = meta.getAttribute('data-total-estimate');
            if (metaTotalEstimate !== null) {
                pager.setAttribute('data-total-estimate', metaTotalEstimate);
            }
            setHasMore(meta.getAttribute('data-has-more') === '1');
        } else if (appended < limit) {
            setHasMore(false);
        }

        return appended;
    };
    const loadNext = () => {
        if (pendingPageRequest) {
            return pendingPageRequest;
        }
        if (!hasMore()) {
            return Promise.resolve(0);
        }

        loader.hidden = false;
        // Filtering may request the full channel while lazy pagination is already in flight.
        // Join that page instead of reporting an empty page and stopping loadAll prematurely.
        const request = new Promise((resolve, reject) => requestTeamContentsHtmlByRest(
            resolvedTeamId,
            accessToken,
            limit,
            (html) => {
                const appended = appendCards(html);
                resolve(appended);
            },
            (error) => {
                loader.hidden = !hasMore();
                notifyTeamContentsLoadError();
                reject(error);
            },
            {
                offset: nextOffset,
                fragment: 'items',
                paged: true
            }
        )).finally(() => {
            if (pendingPageRequest === request) pendingPageRequest = null;
        });
        pendingPageRequest = request;
        return request;
    };

    const controller = {
        hasMore,
        loadNext,
        loadAll() {
            if (loadAllPromise) {
                return loadAllPromise;
            }

            loadAllPromise = (async () => {
                while (hasMore()) {
                    const appended = await loadNext();
                    if (!appended) {
                        break;
                    }
                }
            })().finally(() => {
                loadAllPromise = null;
            });

            return loadAllPromise;
        }
    };

    teamContentPagerControllers.set(pager, controller);
    setHasMore(hasMore());

    if (!hasMore()) {
        return controller;
    }

    if (!('IntersectionObserver' in window)) {
        loadNext();
        return controller;
    }

    const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
            loadNext().catch(() => {});
        }
    }, { rootMargin: '640px' });

    observer.observe(loader);
    return controller;
}

function formatFilterChipLabel(template, label, fallback = 'Remove filter') {
    const normalizedTemplate = String(template || '').trim() || fallback;
    const normalizedLabel = String(label || '').trim();
    const replacement = normalizedLabel === '%s' ? '' : normalizedLabel;
    const hasPlaceholder = /%1\$s|%s/.test(normalizedTemplate);
    const placeholderPattern = /%1\$s|%s/g;

    if (!hasPlaceholder) {
        return [normalizedTemplate, replacement].filter(Boolean).join(' ').trim() || fallback;
    }

    if (!replacement) {
        return normalizedTemplate
            .replace(placeholderPattern, '')
            .replace(/\s{2,}/g, ' ')
            .trim() || fallback;
    }

    return normalizedTemplate
        .replace(placeholderPattern, replacement)
        .replace(/\s{2,}/g, ' ')
        .trim();
}

function initTeamContentFilter() {
    const filter = document.querySelector('[data-team-content-filter]');
    if (!filter) {
        return;
    }

    const targetSelector = filter.getAttribute('data-target') || '.content-detail-teams';
    const container = document.querySelector(targetSelector);
    const searchInput = filter.querySelector('[data-team-filter-search]');
    const tagSelect = filter.querySelector('[data-team-filter-tag]');
    const chipsElement = filter.querySelector('[data-team-filter-chips]');
    const resetButton = filter.querySelector('[data-team-filter-reset]');
    const countElement = filter.querySelector('[data-team-filter-count]');
    const emptyElement = document.querySelector('[data-team-filter-empty]');

    if (!container || !searchInput || !tagSelect || !chipsElement || !resetButton || !countElement) {
        return;
    }

    const filterText = window.khThemeRuntime?.teamContentFilter || {};
    const filterLocale = (window.khThemeRuntime?.locale || document.documentElement.lang || '')
        .replace('_', '-')
        .trim() || undefined;
    const translate = (key, fallback) => filterText[key] || fallback;
    const formatCount = (visible, total, totalIsEstimate = false) => {
        const template = translate('count', '%1$s of %2$s contents');
        const totalLabel = totalIsEstimate ? String(total) + '+' : String(total);
        return template
            .replace('%1$s', visible)
            .replace('%2$s', totalLabel);
    };
    const formatRemoveFilter = (label) => formatFilterChipLabel(
        translate('removeFilter', 'Remove filter %s'),
        label
    );
    const normalize = (value) => String(value || '').trim().toLowerCase();
    const selectedTags = new Map();
    const typeLabels = {
        articles: translate('typeArticle', 'Article'),
        article: translate('typeArticle', 'Article'),
        event: translate('typeEvent', 'Event'),
        content_group: translate('typeCourse', 'Course'),
        course: translate('typeCourse', 'Course'),
        test: translate('typeTest', 'Test'),
        method: translate('typeMethod', 'Method'),
        certificate: translate('typeCertificate', 'Certificate')
    };

    const getCards = () => Array.from(container.querySelectorAll('.t-button-directory'));
    const getCardTypeLabel = (card) => {
        const kind = normalize(card.getAttribute('data-content-kind') || card.getAttribute('data-load-contenttype'));
        return typeLabels[kind] || translate('typeContent', 'Content');
    };
    const getCardTags = (card) => {
        const tags = Array.from(card.querySelectorAll('.t-tag-wrapper .t-tag'))
            .map((tag) => tag.textContent.trim())
            .filter(Boolean);

        tags.push(getCardTypeLabel(card));
        return Array.from(new Set(tags));
    };
    const updateTagOptions = (cards) => {
        const options = new Map();

        cards.forEach((card) => {
            getCardTags(card).forEach((label) => {
                const value = normalize(label);
                if (value) {
                    options.set(value, label);
                }
            });
        });

        Array.from(selectedTags.keys()).forEach((value) => {
            if (!options.has(value)) {
                selectedTags.delete(value);
            }
        });

        tagSelect.innerHTML = '';
        const defaultOption = document.createElement('option');
        defaultOption.value = '';
        defaultOption.textContent = translate('allTopics', 'Alle Themen und Typen');
        tagSelect.appendChild(defaultOption);
        Array.from(options.entries())
            .sort((a, b) => {
                try {
                    return a[1].localeCompare(b[1], filterLocale);
                } catch (e) {
                    return a[1].localeCompare(b[1]);
                }
            })
            .forEach(([value, label]) => {
                if (selectedTags.has(value)) {
                    return;
                }

                const option = document.createElement('option');
                option.value = value;
                option.textContent = label;
                tagSelect.appendChild(option);
            });

        tagSelect.value = '';
    };
    const renderChips = () => {
        chipsElement.innerHTML = '';
        chipsElement.hidden = selectedTags.size === 0;

        selectedTags.forEach((label, value) => {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'kh-team-content-filter__chip';
            chip.setAttribute('data-team-filter-chip', value);
            chip.setAttribute('aria-label', formatRemoveFilter(label));
            chip.innerHTML = `<span>${label}</span><span aria-hidden="true">×</span>`;
            chipsElement.appendChild(chip);
        });
    };
    const placeFilterInContentSection = () => {
        const sectionHead = container.querySelector('.active-team-content .t-kh-home-row-h');
        if (!sectionHead || sectionHead.nextElementSibling === filter) {
            return;
        }

        sectionHead.after(filter);
        if (emptyElement) {
            filter.after(emptyElement);
        }
    };
    const applyFilter = () => {
        const cards = getCards();
        const searchTerm = normalize(searchInput.value);
        const activeTags = Array.from(selectedTags.keys());
        let visibleCount = 0;
        const pager = container.querySelector('[data-team-content-pager]');
        const totalCount = parseInt(pager?.getAttribute('data-total') || cards.length, 10) || cards.length;
        const totalIsEstimate = pager?.getAttribute('data-total-estimate') === '1';

        cards.forEach((card) => {
            const tags = getCardTags(card).map(normalize);
            const haystack = normalize(card.textContent);
            const matchesSearch = !searchTerm || haystack.includes(searchTerm);
            const matchesTag = activeTags.length === 0 || activeTags.every((tag) => tags.includes(tag));
            const isVisible = matchesSearch && matchesTag;

            card.hidden = !isVisible;
            card.classList.toggle('kh-team-filter-hidden', !isVisible);

            if (isVisible) {
                visibleCount++;
            }
        });

        countElement.textContent = cards.length || totalCount || pager
            ? formatCount(visibleCount, totalCount, totalIsEstimate)
            : translate('loading', 'Loading content...');

        if (emptyElement) {
            emptyElement.hidden = !cards.length || visibleCount > 0;
        }
    };
    let filterSyncScheduled = false;
    const syncAndApply = () => {
        filterSyncScheduled = false;
        const cards = getCards();
        placeFilterInContentSection();
        updateTagOptions(cards);
        renderChips();
        applyFilter();
    };
    let pendingFullDatasetSync = null;
    const syncAfterFullDataset = (promise, filterOnError = false) => {
        if (pendingFullDatasetSync?.promise === promise) {
            if (filterOnError) pendingFullDatasetSync.filterOnError = true;
            return;
        }

        // Typing and topic focus may all await the same page drain. Rebuild the filter once,
        // using its current inputs, when that shared load completes.
        const pending = {promise, filterOnError};
        pendingFullDatasetSync = pending;
        promise.then(() => {
            if (pendingFullDatasetSync === pending) syncAndApply();
        }).catch(() => {
            if (pendingFullDatasetSync === pending && pending.filterOnError) applyFilter();
        }).finally(() => {
            if (pendingFullDatasetSync === pending) pendingFullDatasetSync = null;
        });
    };
    const applyFilterWithFullDataset = () => {
        const needsFullDataset = normalize(searchInput.value) || selectedTags.size > 0;
        const loadPromise = needsFullDataset ? ensureTeamContentPagerLoaded(container) : null;

        if (!loadPromise) {
            applyFilter();
            return;
        }

        countElement.textContent = translate('loading', 'Loading content...');
        syncAfterFullDataset(loadPromise, true);
    };

    searchInput.addEventListener('input', applyFilterWithFullDataset);
    tagSelect.addEventListener('focus', () => {
        const loadPromise = ensureTeamContentPagerLoaded(container);
        if (loadPromise) {
            syncAfterFullDataset(loadPromise);
        }
    });
    tagSelect.addEventListener('change', () => {
        const value = tagSelect.value;
        const option = tagSelect.selectedOptions[0];

        if (value && option && !selectedTags.has(value)) {
            selectedTags.set(value, option.textContent.trim());
        }

        tagSelect.value = '';
        updateTagOptions(getCards());
        renderChips();
        applyFilterWithFullDataset();
    });
    chipsElement.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-team-filter-chip]');
        if (!chip) {
            return;
        }

        selectedTags.delete(chip.getAttribute('data-team-filter-chip'));
        updateTagOptions(getCards());
        renderChips();
        applyFilterWithFullDataset();
    });
    resetButton.addEventListener('click', () => {
        searchInput.value = '';
        tagSelect.value = '';
        selectedTags.clear();
        updateTagOptions(getCards());
        renderChips();
        applyFilter();
        searchInput.focus();
    });

    if ('MutationObserver' in window) {
        const observer = new MutationObserver((records) => {
            // Cards are appended inside the nested list. Ignore our own select/chip
            // changes, and let a pending full-channel load synchronize once on completion.
            const contentChanged = records.some((record) => record.target === container
                || record.target.matches?.('[data-team-content-list]'));
            if (!contentChanged || pendingFullDatasetSync || filterSyncScheduled) {
                return;
            }
            filterSyncScheduled = true;
            window.requestAnimationFrame(() => {
                if (!filterSyncScheduled) return;
                filterSyncScheduled = false;
                if (!pendingFullDatasetSync) syncAndApply();
            });
        });

        observer.observe(container, { childList: true, subtree: true });
    }

    syncAndApply();
}

executeWhenReady(initTeamContentFilter);

function initCertificateContentFilter() {
    const filter = document.querySelector('[data-certificate-content-filter]');
    if (!filter) {
        return;
    }
    if (filter.dataset.certificateFilterInitialized === '1') {
        return;
    }
    filter.dataset.certificateFilterInitialized = '1';

    const targetSelector = filter.getAttribute('data-target') || '.t-row-single.certificate';
    const container = document.querySelector(targetSelector);
    const searchInput = filter.querySelector('[data-certificate-filter-search]');
    const channelSelect = filter.querySelector('[data-certificate-filter-channel]');
    const kindSelect = filter.querySelector('[data-certificate-filter-kind]');
    const statusSelect = filter.querySelector('[data-certificate-filter-status]');
    const chipsElement = filter.querySelector('[data-certificate-filter-chips]');
    const resetButtons = Array.from(document.querySelectorAll('[data-certificate-filter-reset]'));
    const countElement = document.querySelector('[data-certificate-filter-count]');
    const emptyElement = document.querySelector('[data-certificate-filter-empty]');
    const resultsElement = document.querySelector('[data-certificate-filter-results]');
    const overviewFilterButtons = Array.from(document.querySelectorAll('[data-certificate-overview-filter]'));
    const listElement = document.querySelector('[data-certificate-list]');
    const listBody = listElement?.querySelector('[data-certificate-list-body]');
    const listSortButtons = Array.from(document.querySelectorAll('[data-certificate-list-sort]'));
    const viewToggleButtons = Array.from(document.querySelectorAll('[data-certificate-view-toggle]'));
    const viewPanels = Array.from(document.querySelectorAll('[data-certificate-view-panel]'));
    const filterToggleButton = document.querySelector('[data-certificate-filter-toggle]');
    const filterPopover = document.querySelector('[data-certificate-filter-popover]');
    const filterCloseButton = document.querySelector('[data-certificate-filter-close]');

    if (!container || !searchInput || !channelSelect || !kindSelect || !statusSelect || !chipsElement || resetButtons.length === 0 || !countElement) {
        return;
    }

    const filterText = window.khThemeRuntime?.certificateContentFilter || {};
    const filterLocale = (window.khThemeRuntime?.locale || document.documentElement.lang || '')
        .replace('_', '-')
        .trim() || undefined;
    const translate = (key, fallback) => filterText[key] || fallback;
    const normalize = (value) => String(value || '').trim().toLowerCase();
    const formatCount = (visible, total) => {
        const template = translate('count', '%1$s of %2$s certificates');
        return template
            .replace('%1$s', visible)
            .replace('%2$s', total);
    };
    const formatRemoveFilter = (label) => formatFilterChipLabel(
        translate('removeFilter', 'Remove filter %s'),
        label
    );
    const selectedChannels = new Map();
    const apiStatuses = ['not_started', 'open', 'earnable', 'valid', 'expiring_soon', 'expired'];
    const defaultEmptyMessage = emptyElement ? emptyElement.textContent.trim() : '';
    const overviewEmptyMessages = new Map();
    overviewFilterButtons.forEach((button) => {
        const kind = button.getAttribute('data-certificate-filter-kind-target') || 'all';
        const status = button.getAttribute('data-certificate-filter-status-target') || 'all';
        const message = button.getAttribute('data-certificate-filter-empty-message') || '';

        if (message) {
            overviewEmptyMessages.set(`${kind}:${status}`, message);
        }
    });
    const hasActiveFilters = () => (
        normalize(searchInput.value) !== '' ||
        (kindSelect.value && kindSelect.value !== 'all') ||
        (statusSelect.value && statusSelect.value !== 'all') ||
        selectedChannels.size > 0
    );
    const syncResetButtons = () => {
        const isActive = hasActiveFilters();

        resetButtons.forEach((button) => {
            button.disabled = !isActive;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-disabled', isActive ? 'false' : 'true');
        });
    };
    const setCertificateView = (view) => {
        const activeView = view === 'table' ? 'table' : 'cards';

        viewToggleButtons.forEach((button) => {
            const isActive = button.getAttribute('data-certificate-view-toggle') === activeView;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        viewPanels.forEach((panel) => {
            const isActive = panel.getAttribute('data-certificate-view-panel') === activeView;
            panel.hidden = !isActive;
        });
    };
    const setFilterPopoverOpen = (isOpen) => {
        if (!filterToggleButton || !filterPopover) {
            return;
        }

        filterPopover.hidden = !isOpen;
        filterToggleButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    };

    const getCards = () => Array.from(container.querySelectorAll('.t-button-directory'))
        .filter((card) => card.style.display !== 'none');
    const getListRows = () => listBody ? Array.from(listBody.querySelectorAll('[data-certificate-list-row]')) : [];
    const matchesKind = (card, kind) => {
        if (!kind || kind === 'all') {
            return true;
        }

        const cardKind = normalize(card.getAttribute('data-certificate-kind')) || 'certificate';
        return cardKind === kind;
    };
    const matchesStatus = (card, status) => {
        const apiStatus = normalize(card.getAttribute('data-certificate-status'));
        if (apiStatuses.includes(status)) {
            if (status === 'expired' && card.dataset.renewalExpired === '1') {
                return true;
            }
            if (apiStatus) {
                return apiStatus === status;
            }

            switch (status) {
                case 'not_started':
                    return card.dataset.finished !== '1' && card.dataset.overDue !== '1';
                case 'open':
                    return card.dataset.finished !== '1' && card.dataset.overDue !== '1';
                case 'earnable':
                    return card.dataset.finished === '1';
                case 'valid':
                    return card.dataset.validUntil === '1' || card.classList.contains('certificate-valid');
                case 'expiring_soon':
                    return card.dataset.dueSoon === '1';
                case 'expired':
                    return card.dataset.overDue === '1';
            }
        }

        switch (status) {
            case 'due':
                return card.dataset.due === '1';
            case 'overdue':
                return card.dataset.overDue === '1';
            case 'due-soon':
                return card.dataset.dueSoon === '1';
            case 'valid-until':
                return card.dataset.validUntil === '1' || card.classList.contains('certificate-valid');
            case 'finished':
                return card.dataset.finished === '1';
            case 'all':
            default:
                return true;
        }
    };
    const getChannelLabel = (card) => {
        const channelName = card.getAttribute('data-certificate-channel-name');
        if (channelName) {
            return channelName;
        }

        const channelId = card.getAttribute('data-certificate-channel-id');
        return channelId ? translate('channelFallback', 'Channel %s').replace('%s', channelId) : '';
    };
    const updateChannelOptions = (cards) => {
        const options = new Map();

        cards.forEach((card) => {
            const channelId = card.getAttribute('data-certificate-channel-id');
            const channelLabel = getChannelLabel(card);
            if (channelId && channelLabel) {
                options.set(channelId, channelLabel);
            }
        });

        Array.from(selectedChannels.keys()).forEach((value) => {
            if (!options.has(value)) {
                selectedChannels.delete(value);
            }
        });

        channelSelect.innerHTML = '';
        const defaultOption = document.createElement('option');
        defaultOption.value = '';
        defaultOption.textContent = translate('allChannels', 'All channels');
        channelSelect.appendChild(defaultOption);
        Array.from(options.entries())
            .sort((a, b) => {
                try {
                    return a[1].localeCompare(b[1], filterLocale);
                } catch (e) {
                    return a[1].localeCompare(b[1]);
                }
            })
            .forEach(([value, label]) => {
                if (selectedChannels.has(value)) {
                    return;
                }

                const option = document.createElement('option');
                option.value = value;
                option.textContent = label;
                channelSelect.appendChild(option);
            });

        channelSelect.value = '';
    };
    const renderChips = () => {
        chipsElement.innerHTML = '';
        chipsElement.hidden = selectedChannels.size === 0;

        selectedChannels.forEach((label, value) => {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'kh-team-content-filter__chip';
            chip.setAttribute('data-certificate-filter-chip', value);
            chip.setAttribute('aria-label', formatRemoveFilter(label));
            chip.innerHTML = `<span>${label}</span><span aria-hidden="true">×</span>`;
            chipsElement.appendChild(chip);
        });
    };
    const itemMatchesActiveFilters = (item, searchTerm, activeKind, activeStatus, activeChannels) => {
        const channelId = item.getAttribute('data-certificate-channel-id') || '';
        const haystack = normalize(item.textContent + ' ' + getChannelLabel(item));
        const matchesSearch = !searchTerm || haystack.includes(searchTerm);
        const matchesKindFilter = matchesKind(item, activeKind);
        const matchesStatusFilter = matchesStatus(item, activeStatus);
        const matchesChannel = activeChannels.length === 0 || activeChannels.includes(channelId);

        return matchesSearch && matchesKindFilter && matchesStatusFilter && matchesChannel;
    };
    const getEmptyMessage = (activeKind, activeStatus, searchTerm, activeChannels) => {
        if (searchTerm || activeChannels.length > 0) {
            return defaultEmptyMessage;
        }

        return overviewEmptyMessages.get(`${activeKind}:${activeStatus}`) || defaultEmptyMessage;
    };
    const applyFilter = () => {
        const cards = getCards();
        const rows = getListRows();
        const searchTerm = normalize(searchInput.value);
        const activeKind = kindSelect.value || 'all';
        const activeStatus = statusSelect.value || 'all';
        const activeChannels = Array.from(selectedChannels.keys());
        let visibleCount = 0;

        cards.forEach((card) => {
            const isVisible = itemMatchesActiveFilters(card, searchTerm, activeKind, activeStatus, activeChannels);

            card.hidden = !isVisible;
            card.classList.toggle('kh-team-filter-hidden', !isVisible);

            if (isVisible) {
                visibleCount++;
            }
        });

        rows.forEach((row) => {
            const isVisible = itemMatchesActiveFilters(row, searchTerm, activeKind, activeStatus, activeChannels);

            row.hidden = !isVisible;
            row.classList.toggle('kh-team-filter-hidden', !isVisible);
        });

        countElement.textContent = formatCount(visibleCount, cards.length);

        if (emptyElement) {
            emptyElement.textContent = getEmptyMessage(activeKind, activeStatus, searchTerm, activeChannels);
            emptyElement.hidden = !cards.length || visibleCount > 0;
        }

        if (resultsElement) {
            resultsElement.hidden = false;
        }

        syncResetButtons();
    };
    const syncAndApply = () => {
        const cards = getCards();
        updateChannelOptions(cards);
        renderChips();
        applyFilter();
    };
    const setSelectValue = (selectElement, value, fallback = '') => {
        const normalizedValue = String(value || fallback);
        const hasOption = Array.from(selectElement.options).some((option) => option.value === normalizedValue);
        selectElement.value = hasOption ? normalizedValue : fallback;
    };
    const getInitialUrlFilterValue = (keys) => {
        try {
            const params = new URLSearchParams(window.location.search);
            const keyList = Array.isArray(keys) ? keys : [keys];

            for (const key of keyList) {
                const value = params.get(key);
                if (value) {
                    return value;
                }
            }
        } catch (e) {
            return '';
        }

        return '';
    };
    const updateCertificateFilterUrl = (kind, status) => {
        if (!window.history?.replaceState) {
            return;
        }

        try {
            const url = new URL(window.location.href);
            if (kind && kind !== 'all') {
                url.searchParams.set('certificate_kind', kind);
            } else {
                url.searchParams.delete('certificate_kind');
            }

            if (status && status !== 'all') {
                url.searchParams.set('certificate_status', status);
            } else {
                url.searchParams.delete('certificate_status');
            }

            window.history.replaceState({}, '', url.toString());
        } catch (e) {
            // URL state is progressive enhancement; filtering still works without it.
        }
    };
    const sortList = (sortKey, direction) => {
        if (!listBody || !sortKey) {
            return;
        }

        const nextDirection = direction || 'asc';
        const rows = getListRows();
        rows.sort((rowA, rowB) => {
            const valueA = Number(rowA.getAttribute(`data-sort-${sortKey}`) || '9999999999');
            const valueB = Number(rowB.getAttribute(`data-sort-${sortKey}`) || '9999999999');
            const isEmptyA = valueA >= 9999999999;
            const isEmptyB = valueB >= 9999999999;
            if (isEmptyA && !isEmptyB) {
                return 1;
            }
            if (!isEmptyA && isEmptyB) {
                return -1;
            }

            const result = valueA - valueB;

            return nextDirection === 'desc' ? result * -1 : result;
        });
        rows.forEach((row) => listBody.appendChild(row));
        listSortButtons.forEach((button) => {
            const isActive = button.getAttribute('data-certificate-list-sort') === sortKey;
            button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            button.setAttribute('data-sort-direction', isActive ? nextDirection : '');
        });
    };

    searchInput.addEventListener('input', applyFilter);
    kindSelect.addEventListener('change', () => {
        updateCertificateFilterUrl(kindSelect.value, statusSelect.value);
        applyFilter();
    });
    statusSelect.addEventListener('change', () => {
        updateCertificateFilterUrl(kindSelect.value, statusSelect.value);
        applyFilter();
    });
    channelSelect.addEventListener('change', () => {
        const value = channelSelect.value;
        const option = channelSelect.selectedOptions[0];

        if (value && option && !selectedChannels.has(value)) {
            selectedChannels.set(value, option.textContent.trim());
        }

        channelSelect.value = '';
        updateChannelOptions(getCards());
        renderChips();
        applyFilter();
    });
    chipsElement.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-certificate-filter-chip]');
        if (!chip) {
            return;
        }

        selectedChannels.delete(chip.getAttribute('data-certificate-filter-chip'));
        updateChannelOptions(getCards());
        renderChips();
        applyFilter();
    });
    const resetCertificateFilters = (event) => {
        searchInput.value = '';
        channelSelect.value = '';
        kindSelect.value = 'all';
        statusSelect.value = 'all';
        selectedChannels.clear();
        updateCertificateFilterUrl('all', 'all');
        updateChannelOptions(getCards());
        renderChips();
        applyFilter();
        if (event?.currentTarget && filterPopover && !filterPopover.hidden && filterPopover.contains(event.currentTarget)) {
            searchInput.focus();
        } else if (event?.currentTarget) {
            event.currentTarget.focus();
        }
    };
    resetButtons.forEach((button) => {
        button.addEventListener('click', resetCertificateFilters);
    });
    if (filterToggleButton && filterPopover) {
        filterToggleButton.addEventListener('click', (event) => {
            event.stopPropagation();
            setFilterPopoverOpen(filterPopover.hidden);
        });
        filterPopover.addEventListener('click', (event) => {
            event.stopPropagation();
        });
        if (filterCloseButton) {
            filterCloseButton.addEventListener('click', () => {
                setFilterPopoverOpen(false);
                filterToggleButton.focus();
            });
        }
        document.addEventListener('click', (event) => {
            if (
                filterPopover.hidden ||
                filterPopover.contains(event.target) ||
                filterToggleButton.contains(event.target)
            ) {
                return;
            }

            setFilterPopoverOpen(false);
        });
        document.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape' || filterPopover.hidden) {
                return;
            }

            setFilterPopoverOpen(false);
            filterToggleButton.focus();
        });
    }
    overviewFilterButtons.forEach((button) => {
        button.addEventListener('click', () => {
            const nextKind = button.getAttribute('data-certificate-filter-kind-target');
            const nextStatus = button.getAttribute('data-certificate-filter-status-target');
            searchInput.value = '';
            selectedChannels.clear();
            setSelectValue(kindSelect, nextKind, 'all');
            setSelectValue(statusSelect, nextStatus, 'all');
            updateCertificateFilterUrl(nextKind, nextStatus);
            updateChannelOptions(getCards());
            renderChips();
            applyFilter();

            (resultsElement || filter).scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        });
    });
    listSortButtons.forEach((button) => {
        button.addEventListener('click', () => {
            const sortKey = button.getAttribute('data-certificate-list-sort');
            const currentDirection = button.getAttribute('data-sort-direction') || 'desc';
            const nextDirection = currentDirection === 'asc' ? 'desc' : 'asc';

            sortList(sortKey, nextDirection);
        });
    });
    viewToggleButtons.forEach((button) => {
        button.addEventListener('click', () => {
            setCertificateView(button.getAttribute('data-certificate-view-toggle'));
        });
    });

    setCertificateView('cards');
    setSelectValue(kindSelect, getInitialUrlFilterValue(['certificate_kind', 'certificate_filter_kind']), 'all');
    setSelectValue(statusSelect, getInitialUrlFilterValue(['certificate_status', 'certificate_filter_status']), 'all');
    syncAndApply();
    sortList('due', 'asc');
}

executeWhenReady(initCertificateContentFilter);

function getKhRestBaseUrl() {
    return String(window.khThemeRuntime?.restBaseUrl || '/wp-json/knowhub/v2').replace(/\/+$/, '');
}

function getKhFrontendLocale() {
    const locale = window.khThemeRuntime?.frontendLocale || 'de_DE';
    return typeof locale === 'string' ? locale.trim() : '';
}

function normalizeHtmlResponse(data) {
    if (typeof data === 'string') {
        return data;
    }
    if (data && typeof data.html === 'string') {
        return data.html;
    }
    if (data && typeof data.data === 'string') {
        return data.data;
    }
    return '';
}

function setKhFrontendLocale(locale, options = {}) {
    const normalizedLocale = String(locale || '').trim();
    if (!normalizedLocale) {
        return '';
    }

    window.khThemeRuntime = window.khThemeRuntime || {};
    window.khThemeRuntime.frontendLocale = normalizedLocale;

    if (options.abortPending !== false && window.TeejitRestClient?.abortPendingRequests) {
        window.TeejitRestClient.abortPendingRequests('frontend-locale-change');
    }

    if (window.TeejitRestClient?.invalidateRuntimeCaches) {
        window.TeejitRestClient.invalidateRuntimeCaches();
    }

    return normalizedLocale;
}

function khSoftRefreshHeaders(locale) {
    const accessToken = String(window.khThemeRuntime?.userAccessToken || '');

    return {
        Accept: 'text/html',
        ...(accessToken ? {Authorization: 'Bearer ' + accessToken} : {}),
        ...(locale ? {'X-Frontend-Locale': locale} : {}),
    };
}

function khCurrentUrlWithFrontendLocale(locale) {
    const url = new URL(window.location.href);
    url.searchParams.set('frontend_locale', locale);
    url.searchParams.set('_kh_soft_refresh', String(Date.now()));

    return url.toString();
}

function khCurrentDirectoryContentId() {
    const url = new URL(window.location.href);
    if (!url.pathname.replace(/\/+$/, '').endsWith('/teejit/directory')) {
        return 0;
    }

    const contentId = Number(url.searchParams.get('id'));
    return Number.isSafeInteger(contentId) && contentId > 0 ? contentId : 0;
}

async function khFetchLocalizedDirectoryContent(contentId, locale, signal) {
    const accessToken = String(window.khThemeRuntime?.userAccessToken || '');
    const url = new URL(getKhRestBaseUrl() + '/content/' + encodeURIComponent(contentId) + '/html', window.location.origin);
    url.searchParams.set('frontend_locale', locale);

    const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json',
            ...(accessToken ? {Authorization: 'Bearer ' + accessToken} : {}),
            'X-Frontend-Locale': locale,
        },
        ...(signal ? {signal} : {}),
    });
    if (!response.ok) {
        throw new Error('kh_frontend_locale_content_refresh_failed');
    }

    const payload = await response.json();
    if (payload?.success !== true || typeof payload.html !== 'string' || payload.html.trim().length <= 10) {
        throw new Error('kh_frontend_locale_content_refresh_missing_content');
    }

    return payload.html;
}

function khReplaceHtmlFromDocument(nextDocument, selector, mode = 'outer') {
    const currentElement = document.querySelector(selector);
    const nextElement = nextDocument.querySelector(selector);

    if (!currentElement || !nextElement) {
        return false;
    }

    if (mode === 'inner') {
        currentElement.innerHTML = nextElement.innerHTML;
    } else {
        currentElement.replaceWith(nextElement);
    }

    return true;
}

function khExtractLocalizedRuntime(nextDocument) {
    const scripts = Array.from(nextDocument.querySelectorAll('script'));
    const runtimeScript = scripts.find((script) => /\bvar\s+khThemeRuntime\s*=/.test(script.textContent || ''));

    if (!runtimeScript) {
        return null;
    }

    const scriptText = runtimeScript.textContent || '';
    const match = scriptText.match(/\bvar\s+khThemeRuntime\s*=\s*(\{[\s\S]*?\});/);

    if (!match || !match[1]) {
        return null;
    }

    try {
        return JSON.parse(match[1]);
    } catch (error) {
        if (window.khThemeRuntime?.debugAjax) {
            console.warn('Localized runtime could not be parsed from soft refresh response.', error);
        }
    }

    return null;
}

function khMergeLocalizedRuntime(nextRuntime, locale) {
    if (!nextRuntime || typeof nextRuntime !== 'object') {
        setKhFrontendLocale(locale);
        return;
    }

    const currentRuntime = window.khThemeRuntime || {};
    window.khThemeRuntime = {
        ...currentRuntime,
        ...nextRuntime,
        userAccessToken: currentRuntime.userAccessToken || nextRuntime.userAccessToken || '',
        userId: currentRuntime.userId || nextRuntime.userId || '',
        userProfileImage: currentRuntime.userProfileImage || nextRuntime.userProfileImage || '',
        profileInitials: currentRuntime.profileInitials || nextRuntime.profileInitials || '',
        frontendLocale: locale || nextRuntime.frontendLocale || currentRuntime.frontendLocale || '',
    };

    if (window.TeejitRestClient?.invalidateRuntimeCaches) {
        window.TeejitRestClient.invalidateRuntimeCaches();
    }
}

function khReinitializeAppShell() {
    initTeejit_MobileHeadNav();
    initTeejit_ProfileMenu();
    initTeejit_ProfileLanguageSwitcher();
}

function khReinitializeDynamicView() {
    initTeejit_accordion();
    initTeejit_ContentLazyItems();
    initTeejit_LabelFilter();
    initCertificateContentFilter();
    initCertificateDetailLoader();
    initCertificateRequirementLoader();
    init_Teejit_TeejitEditProfilData();

    if (typeof window.khBootKnowledgeHubTour === 'function') {
        window.khBootKnowledgeHubTour();
    }

    if (document.getElementById('kh-home-api-fragments-config') && window.TeejitHomeHydrator?.boot) {
        if (window.khHomeHydrator?.refreshFromDom) {
            window.khHomeHydrator.refreshFromDom();
        } else {
            window.TeejitHomeHydrator.boot();
        }
    }
}

async function khSoftRefreshCurrentFrontendView(locale) {
    const frontendLocale = setKhFrontendLocale(locale);
    if (!frontendLocale) {
        return false;
    }

    if (khFrontendLocaleRefreshController && typeof khFrontendLocaleRefreshController.abort === 'function') {
        khFrontendLocaleRefreshController.abort('frontend-locale-change');
    }

    const refreshSequence = ++khFrontendLocaleRefreshSequence;
    const refreshController = typeof window.AbortController === 'function'
        ? new window.AbortController()
        : null;
    khFrontendLocaleRefreshController = refreshController;

    document.documentElement.classList.add('kh-frontend-soft-refreshing');
    let contentWrapper = document.getElementById('t-content-wrapper');
    if (contentWrapper) {
        contentWrapper.setAttribute('aria-busy', 'true');
    }

    try {
        document.dispatchEvent(new CustomEvent('kh:frontendLocaleChanging', {
            detail: {locale: frontendLocale},
        }));

        const pageRequest = fetch(khCurrentUrlWithFrontendLocale(frontendLocale), {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: khSoftRefreshHeaders(frontendLocale),
            ...(refreshController ? {signal: refreshController.signal} : {}),
        }).then((response) => {
            if (!response.ok) {
                throw new Error('kh_frontend_locale_soft_refresh_failed');
            }
            return response.text();
        });
        const directoryContentId = khCurrentDirectoryContentId();
        const directoryRequest = directoryContentId
            ? khFetchLocalizedDirectoryContent(directoryContentId, frontendLocale, refreshController?.signal)
            : Promise.resolve(null);
        const [html, directoryHtml] = await Promise.all([pageRequest, directoryRequest]);
        if (refreshSequence !== khFrontendLocaleRefreshSequence) {
            return false;
        }

        const nextDocument = new DOMParser().parseFromString(html, 'text/html');
        khMergeLocalizedRuntime(khExtractLocalizedRuntime(nextDocument), frontendLocale);

        khReplaceHtmlFromDocument(nextDocument, '#t-nav');
        khReplaceHtmlFromDocument(nextDocument, '.kh-app-topbar');
        const contentReplaced = khReplaceHtmlFromDocument(nextDocument, '#t-content-wrapper');

        khReplaceHtmlFromDocument(nextDocument, '#t-nav-left-team-list', 'inner');

        if (!contentReplaced) {
            throw new Error('kh_frontend_locale_soft_refresh_missing_content');
        }

        if (directoryHtml !== null) {
            jQuery('#t-content-wrapper').html(directoryHtml);
        }

        khReinitializeAppShell();
        khReinitializeDynamicView();
        document.dispatchEvent(new CustomEvent('kh:frontendLocaleChanged', {
            detail: {locale: frontendLocale},
        }));

        return true;
    } finally {
        if (refreshSequence === khFrontendLocaleRefreshSequence) {
            khFrontendLocaleRefreshController = null;
            document.documentElement.classList.remove('kh-frontend-soft-refreshing');
            contentWrapper = document.getElementById('t-content-wrapper');
            if (contentWrapper) {
                contentWrapper.removeAttribute('aria-busy');
            }
        }
    }
}

function requestKhRestHtml(method, path, params, access_token, success, error) {
    const httpMethod = String(method || 'GET').toUpperCase();
    const token = String(access_token || window.khThemeRuntime?.userAccessToken || '');
    const payload = {
        ...(params || {}),
    };
    if (token) {
        payload.user_access_token = token;
    }
    const frontendLocale = getKhFrontendLocale();
    if (frontendLocale && !payload.frontend_locale && !payload.locale) {
        payload.frontend_locale = frontendLocale;
    }

    const ajaxOptions = {
        type: httpMethod,
        url: getKhRestBaseUrl() + '/' + String(path || '').replace(/^\/+/, ''),
        dataType: 'json',
        headers: {
            ...(token ? {Authorization: 'Bearer ' + token} : {}),
            ...(frontendLocale ? {'X-Frontend-Locale': frontendLocale} : {}),
        },
        success: function (data) {
            success(normalizeHtmlResponse(data));
        },
        error: error || notifyTeamContentsLoadError,
    };

    if (httpMethod === 'GET') {
        ajaxOptions.data = payload;
    } else {
        ajaxOptions.data = JSON.stringify(payload);
        ajaxOptions.contentType = 'application/json';
    }

    const request = jQuery.ajax(ajaxOptions);
    khActiveRestAjaxRequests.add(request);
    request.always(() => {
        khActiveRestAjaxRequests.delete(request);
    });

    return request;
}

function initTeejit_ContentLazyItems(root = document) {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    const lazyItems = Array.from(scope.querySelectorAll('[data-kh-contentable-lazy="1"]'))
        .filter((item) => item.dataset.khLazyBound !== '1');

    if (!lazyItems.length) {
        return;
    }

    const loadItem = (item) => {
        if (!item || item.dataset.khLazyState === 'loading' || item.dataset.khLazyState === 'loaded') {
            return;
        }

        const contentId = item.dataset.contentId || '';
        const contentableId = item.dataset.contentableId || item.dataset.contentTypeId || '';
        const contentableType = item.dataset.contentableType || '';
        if (!contentId || !contentableId || !contentableType) {
            item.dataset.khLazyState = 'error';
            return;
        }

        item.dataset.khLazyState = 'loading';
        item.classList.add('is-loading');

        requestKhRestHtml(
            'GET',
            'content/' + encodeURIComponent(contentId) + '/items/' + encodeURIComponent(contentableId) + '/html',
            {contentable_type: contentableType},
            null,
            function (html) {
                if (typeof html !== 'string' || html.trim().length < 10) {
                    item.dataset.khLazyState = 'error';
                    item.classList.remove('is-loading');
                    return;
                }

                const template = document.createElement('template');
                template.innerHTML = html.trim();
                const replacement = template.content.firstElementChild;
                if (!replacement) {
                    item.dataset.khLazyState = 'error';
                    item.classList.remove('is-loading');
                    return;
                }

                const wasOpen = Boolean(item.querySelector('details')?.open);
                if (wasOpen) {
                    const replacementDetails = replacement.querySelector('details');
                    if (replacementDetails) {
                        replacementDetails.open = true;
                    }
                }

                replacement.dataset.khContentableLoaded = '1';
                item.replaceWith(replacement);
                khReinitializeDynamicView();
            },
            function () {
                item.dataset.khLazyState = 'error';
                item.classList.remove('is-loading');
                const placeholder = item.querySelector('.kh-content-lazy-placeholder');
                if (placeholder) {
                    placeholder.textContent = window.khThemeRuntime?.contentDetail?.loadError
                        || 'The content exists, but could not be retrieved right now. Please try again later.';
                }
            }
        );
    };

    if (!khContentLazyObserver && 'IntersectionObserver' in window) {
        khContentLazyObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }
                khContentLazyObserver.unobserve(entry.target);
                loadItem(entry.target);
            });
        }, {
            root: null,
            rootMargin: '600px 0px',
            threshold: 0.01,
        });
    }

    lazyItems.forEach((item) => {
        item.dataset.khLazyBound = '1';
        const details = item.querySelector('details');
        if (details) {
            details.addEventListener('toggle', () => {
                if (details.open) {
                    loadItem(item);
                }
            });
        }

        if (details?.open) {
            loadItem(item);
            return;
        }

        if (khContentLazyObserver) {
            khContentLazyObserver.observe(item);
            return;
        }

        window.setTimeout(() => loadItem(item), 100);
    });
}

function renderEmptyTagContentNotice(filtered = false) {
    const title = filtered
        ? khInitMessage('labelNoFilteredContentTitle', 'No matching content found.')
        : khInitMessage('labelNoContentTitle', 'No content available.');
    const message = filtered
        ? khInitMessage('labelNoFilteredContentText', 'There is currently no content for the selected filters.')
        : khInitMessage('labelNoContentText', 'There is currently no content available for this category.');

    return '<div class="t-notice notice-info kh-empty-content-wrapper kh-label-empty-content" role="status" aria-live="polite">' +
        '<h3>' + khEscapeHtml(title) + '</h3>' +
        '<p>' + khEscapeHtml(message) + '</p>' +
        '</div>';
}

function renderTagContentResponse(data, filtered = false) {
    const html = typeof data === 'string' ? data : '';
    const trimmedHtml = html.trim();
    const hasContent = trimmedHtml.includes('t-button-directory') || trimmedHtml.includes('kh-label-empty-content');

    jQuery('section.content-by-tag-id-section').html(
        hasContent ? html : renderEmptyTagContentNotice(filtered)
    );
}

function initCertificateDetailLoader() {
    const app = document.getElementById('kh-certificate-detail-app');
    if (!app || app.dataset.loaded === '1') {
        return;
    }

    const certificateId = app.getAttribute('data-certificate-id');
    if (!certificateId) {
        return;
    }

    const escapeHtml = (value) => String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    const renderLoadError = () => {
        const title = window.khThemeRuntime?.certificateDetail?.loadTitle || 'Certificate could not be loaded.';
        const message = window.khThemeRuntime?.certificateDetail?.loadError || 'The certificate exists, but could not be retrieved right now. Please try again later.';
        app.innerHTML = '<div class="t-notice event-danger kh-certificate-detail-error">' +
            '<h3>' + escapeHtml(title) + '</h3>' +
            '<p>' + escapeHtml(message) + '</p>' +
            '</div>';
    };

    app.dataset.loaded = '1';
    requestKhRestHtml(
        'GET',
        'theme/certificates/' + encodeURIComponent(certificateId) + '/html',
        {},
        null,
        (html) => {
            if (typeof html === 'string' && html.trim().length > 10) {
                app.innerHTML = html;
                initCertificateContentFilter();
                initTeejit_accordion();
                initCertificateRequirementLoader(app);
                return;
            }
            renderLoadError();
        },
        renderLoadError
    );
}

executeWhenReady(initCertificateDetailLoader);

function initCertificateRequirementLoader(root = document) {
    const fragments = Array.from(root.querySelectorAll('[data-certificate-requirement][data-state="idle"]'));
    if (!fragments.length) {
        return;
    }

    const loadFragment = (fragment) => {
        if (!fragment || fragment.dataset.state !== 'idle') {
            return;
        }

        const certificateId = fragment.getAttribute('data-certificate-id');
        const requirementIndex = fragment.getAttribute('data-requirement-index');
        if (!certificateId || requirementIndex === null) {
            return;
        }

        fragment.dataset.state = 'loading';
        const learningUnitIndex = fragment.getAttribute('data-learning-unit-index');
        const requirementPath = 'theme/certificates/' + encodeURIComponent(certificateId) + '/requirements/' + encodeURIComponent(requirementIndex)
            + '/html' + (learningUnitIndex !== null ? '?learning_unit_index=' + encodeURIComponent(learningUnitIndex) : '');
        requestKhRestHtml(
            'GET',
            requirementPath,
            {},
            null,
            (html) => {
                if (typeof html === 'string' && html.trim().length > 0) {
                    fragment.classList.remove('t-kh-loading');
                    fragment.dataset.state = 'loaded';
                    fragment.innerHTML = html;
                    initTeejit_accordion();
                    return;
                }
                fragment.dataset.state = 'error';
                fragment.innerHTML = '<div class="t-notice notice-error">' + khInitMessage('contentLoadError', 'Content could not be loaded.') + '</div>';
            },
            () => {
                fragment.dataset.state = 'error';
                fragment.innerHTML = '<div class="t-notice notice-error">' + khInitMessage('contentLoadError', 'Content could not be loaded.') + '</div>';
            }
        );
    };

    if (!('IntersectionObserver' in window)) {
        fragments.forEach(loadFragment);
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) {
                return;
            }
            observer.unobserve(entry.target);
            loadFragment(entry.target);
        });
    }, { rootMargin: '320px' });

    fragments.forEach((fragment) => observer.observe(fragment));
}

function requestTeamContentsHtmlByRest(team_id, access_token, limit, success, error, options = {}) {
    const params = new URLSearchParams();
    if (access_token) {
        params.set('user_access_token', access_token);
    }
    if (limit !== null && limit !== undefined && limit !== '') {
        params.set('limit', limit);
    }
    if (options.offset !== null && options.offset !== undefined && options.offset !== '') {
        params.set('offset', options.offset);
    }
    if (options.fragment) {
        params.set('fragment', options.fragment);
    }
    if (options.paged) {
        params.set('paged', '1');
    }
    const frontendLocale = getKhFrontendLocale();
    if (frontendLocale) {
        params.set('frontend_locale', frontendLocale);
    }

    const query = params.toString();
    jQuery.ajax({
        type: 'GET',
        url: getKhRestBaseUrl() + '/load-team-contents/' + encodeURIComponent(team_id) + (query ? '?' + query : ''),
        dataType: 'json',
        headers: {
            ...(access_token ? { Authorization: 'Bearer ' + access_token } : {}),
            ...(frontendLocale ? {'X-Frontend-Locale': frontendLocale} : {}),
        },
        success: function (data) {
            success(normalizeHtmlResponse(data));
        },
        error: error
    });
}

function notifyTeamContentsLoadError() {
    const message = window.khThemeRuntime?.teamContentFilter?.loadError || 'Content could not be loaded. Please try again later.';
    if (window.AWN) {
        const notifier = window.KHNotifications?.createNotifier({
            labels: {
                alert: window.khThemeRuntime?.messages?.noticeContent || 'Content'
            }
        }) || new window.AWN({labels: {alert: 'Content'}});
        notifier.alert(message, { durations: { global: 5000 } });
        return;
    }
    console.error(message);
}

/* =====================================================
   Exported AJAX Functions
   Each function performs an AJAX call to load dynamic content.
   The inner function doAjax is executed once the DOM is ready.
   The functions are also attached to the global window object.
   ===================================================== */

/**
 * Retrieves limited team contents via AJAX.
 *
 * @param {string} access_token - The access token for authentication.
 */
export function getTeam_ContentsLimited(access_token) {
    function doAjax() {
        requestKhRestHtml('POST', 'load-team-contents', {}, access_token, function (data) {
                jQuery('#teams-load-spinner').fadeOut();
                if (data?.length > 10) {
                    jQuery('div.t-kh-home-flag-navigation').after(data);
                }
            }, notifyTeamContentsLoadError);
    }
    executeWhenVisible('#teams-load-spinner', doAjax);
}
window.getTeam_ContentsLimited = getTeam_ContentsLimited;

/**
 * Retrieves team contents by team ID (limited) via AJAX.
 *
 * @param {number|string} team_id - The ID of the team.
 * @param {string} access_token - The access token for authentication.
 */
export function getTeam_ContentsLimitedByTeamID(team_id, access_token) {
    function doAjax() {
        const render = function (data) {
            jQuery('#teams-load-spinner-by-teams-id').fadeOut();
            if (data?.length > 10) {
                jQuery('div.teams-content-by-teams-id').html(data);
            }
        };

        requestTeamContentsHtmlByRest(team_id, access_token, null, render, notifyTeamContentsLoadError);
    }
    executeWhenVisible('#teams-load-spinner-by-teams-id', doAjax);
}
window.getTeam_ContentsLimitedByTeamID = getTeam_ContentsLimitedByTeamID;

/**
 * Retrieves team contents by team ID with optional limit via AJAX.
 *
 * @param {number|string} team_id - The team ID.
 * @param {string} access_token - The access token for authentication.
 * @param {number|null} [limit=null] - Optional limit on number of items.
 * @param {string} [spinner_id='teams-load-spinner-by-teams-id'] - The ID of the spinner element.
 * @param {string} [content_id='div.teams-content-by-teams-id'] - The selector for the content container.
 */
export function getTeam_ContentsByTeamID(team_id, access_token, limit = null, spinner_id = 'teams-load-spinner-by-teams-id', content_id = 'div.teams-content-by-teams-id', options = {}) {
    function doAjax() {
        const lazy = Boolean(options.lazy);
        const requestLimit = lazy ? (options.initialLimit || limit || 10) : limit;
        const render = function (data) {
            jQuery('#' + spinner_id).fadeOut();
            if (data?.length > 10) {
                jQuery(content_id).html(data);
                if (lazy) {
                    initLazyTeamContentPager(document.querySelector(content_id), team_id, access_token, requestLimit);
                }
            }
        };

        requestTeamContentsHtmlByRest(team_id, access_token, requestLimit, render, notifyTeamContentsLoadError, {
            paged: lazy,
            offset: 0
        });
    }
    executeWhenVisible('#' + spinner_id, doAjax);
}
window.getTeam_ContentsByTeamID = getTeam_ContentsByTeamID;

/**
 * Retrieves team slug content via AJAX.
 *
 * @param {string} access_token - The access token for authentication.
 */
export function getTeam_SlugContent(access_token) {
    function doAjax() {
        requestKhRestHtml('GET', 'load-team-slug-contents', {}, access_token, function (data) {
                jQuery('#teamsslug-load-spinner').fadeOut();
                if (data?.length > 10) {
                    const recentFragments = jQuery('#kh-home-user-recent-fragments');
                    if (recentFragments.length) {
                        recentFragments.before(data);
                    } else {
                        jQuery('#kh-home-builder-sections').append(data);
                    }
                }
            }, notifyTeamContentsLoadError);
    }
    executeWhenVisible('#teamsslug-load-spinner', doAjax);
}
window.getTeam_SlugContent = getTeam_SlugContent;

/**
 * Retrieves team slug content by team ID via AJAX.
 *
 * @param {string} access_token - The access token.
 * @param {number|string} team_id - The team ID.
 */
export function getTeam_SlugContentByTeamId(access_token, team_id) {
    function doAjax() {
        requestKhRestHtml('GET', 'load-team-slug-contents/' + encodeURIComponent(team_id), {}, access_token, function (data) {
                jQuery('#teams-slug-content-load-spinner-by-teams-id').fadeOut();
                if (data?.length > 10) {
                    jQuery('div.teams-slug-content').before(data);
                }
            }, notifyTeamContentsLoadError);
    }
    executeWhenVisible('#teams-slug-content-load-spinner-by-teams-id', doAjax);
}
window.getTeam_SlugContentByTeamId = getTeam_SlugContentByTeamId;

/**
 * Retrieves the latest team news via AJAX.
 *
 * @param {string} access_token - The access token.
 * @param {number} limit - Maximum number of news items to retrieve.
 */
export function getTeam_GetLatestNews(access_token, limit) {
    function doAjax() {
        requestKhRestHtml('GET', 'theme/latest-news', {limit: limit}, access_token, function (data) {
                if (data?.length > 10) {
                    jQuery('div.latest-teams-news').html(data);
                }
            }, notifyTeamContentsLoadError);
    }
    executeWhenVisible('div.latest-teams-news', doAjax, '120px');
}
window.getTeam_GetLatestNews = getTeam_GetLatestNews;

/**
 * Retrieves content by tag ID via AJAX.
 *
 * @param {string} access_token - The access token.
 * @param {number|string} tag_id - The tag identifier.
 */
export function getContent_ByTagId(access_token, tag_id) {
    function doAjax() {
        requestKhRestHtml('GET', 'theme/tags/' + encodeURIComponent(tag_id) + '/contents', {}, access_token, function (data) {
                renderTagContentResponse(data);
            }, notifyTeamContentsLoadError);
    }
    executeWhenReady(doAjax);
}
window.getContent_ByTagId = getContent_ByTagId;

/**
 * Retrieves content by tag ID with additional filters via AJAX.
 *
 * @param {string} access_token - The access token.
 * @param {number|string} tag_id - The tag identifier.
 * @param {object} filters - Filter parameters to narrow down the content.
 */
export function getContent_ByTagIdWithFilter(access_token, tag_id, filters) {
    function doAjax() {
        requestKhRestHtml('GET', 'theme/tags/' + encodeURIComponent(tag_id) + '/contents', {filters: filters}, access_token, function (data) {
                renderTagContentResponse(data, true);
            }, notifyTeamContentsLoadError);
    }
    executeWhenReady(doAjax);
}
window.getContent_ByTagIdWithFilter = getContent_ByTagIdWithFilter;

/**
 * Retrieves user-marked content via AJAX.
 *
 * @param {string} access_token - The access token.
 */
export function getUser_MarkedContent(access_token) {
    function doAjax() {
        requestKhRestHtml('GET', 'theme/user/marked-content', {}, access_token, function (data) {
                jQuery('#user-load-spinner-marked-content').fadeOut();
                if (data?.length > 10) {
                    jQuery('div.user-marked-content').html(data);
                }
            }, notifyTeamContentsLoadError);
    }
    executeWhenReady(doAjax);
}
window.getUser_MarkedContent = getUser_MarkedContent;

/**
 * Retrieves user-marked content via AJAX.
 *
 * @param {string} access_token - The access token.
 */
export function getUser_UpcomingEventsContent(limit) {
    function doAjax() {
        requestKhRestHtml('GET', 'theme/user/events/upcoming', {limit: limit}, null, function (data) {
                jQuery('#user-load-spinner-events-content').fadeOut();
                if (data?.length > 10) {
                    jQuery('div.user-events-content').html(data);
                }
            }, notifyTeamContentsLoadError);
    }
    executeWhenReady(doAjax);
}
window.getUser_UpcomingEventsContent = getUser_UpcomingEventsContent;


/**
 * Retrieves user-marked content via AJAX.
 *
 * @param {string} access_token - The access token.
 */
export function getUser_RecentEventsContent(limit) {
    function doAjax() {
        requestKhRestHtml('GET', 'theme/user/events/recent', {limit: limit}, null, function (data) {
                jQuery('#user-load-spinner-recent-events-content').fadeOut();
                if (data?.length > 10) {
                    jQuery('div.user-recent-events-content').html(data);
                }
            }, notifyTeamContentsLoadError);
    }
    executeWhenReady(doAjax);
}
window.getUser_RecentEventsContent = getUser_RecentEventsContent;

export function getUser_TeamEventsContent(limit) {
    function doAjax() {
        requestKhRestHtml('GET', 'theme/user/events/teams', {limit: limit}, null, function (data) {
                jQuery('#user-load-spinner-teams-events-content').fadeOut();
                if (data?.length > 10) {
                    jQuery('div.user-teams-events-content').html(data);
                }
            }, notifyTeamContentsLoadError);
    }
    executeWhenReady(doAjax);
}
window.getUser_TeamEventsContent = getUser_TeamEventsContent;

export function getUser_InformationEventsContent(event_id, content_wrapper_id) {
    function doAjax() {
        requestKhRestHtml('GET', 'theme/events/' + encodeURIComponent(event_id) + '/html', {content_wrapper_id: content_wrapper_id}, null, function (data) {
                jQuery('#user-load-spinner-information-events-content').fadeOut();
                if (data?.length > 10) {
                    jQuery('div.user-information-events-content').html(data);
                }
            }, notifyTeamContentsLoadError);
    }
    executeWhenReady(doAjax);
}
window.getUser_InformationEventsContent = getUser_InformationEventsContent;

/**
 * Retrieves detailed content by its ID via AJAX.
 *
 * @param {number|string} content_id - The content identifier.
 */
export function getContent_ContentById(content_id) {
    const escapeHtml = (value) => String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    const renderContentLoadError = (message = null) => {
        const fallbackMessage = window.khThemeRuntime?.contentDetail?.loadTitle
            || window.khThemeRuntime?.teamContentFilter?.loadError
            || 'Content could not be loaded.';
        const safeMessage = String(fallbackMessage);
        const detailMessage = window.khThemeRuntime?.contentDetail?.loadError
            || 'The content exists, but could not be retrieved right now. Please try again later.';
        jQuery('#content-detail-load-spinner').fadeOut();
        jQuery('#t-content-wrapper').html(
            '<div class="t-notice event-danger">' +
            '<h3>' + escapeHtml(safeMessage) + '</h3>' +
            '<p>' + escapeHtml(detailMessage) + '</p>' +
            '</div>'
        );
    };

    const renderHtml = (html) => {
        jQuery('#content-detail-load-spinner').fadeOut();
        if (typeof html === 'string' && html.trim().length > 10) {
            jQuery('#t-content-wrapper').html(html);
            khReinitializeDynamicView();
            return true;
        }
        return false;
    };

    function doAjax() {
        const runtime = window.khThemeRuntime || {};
        const restBaseUrl = String(runtime.restBaseUrl || '').replace(/\/+$/, '');
        const accessToken = String(runtime.userAccessToken || '');
        const frontendLocale = getKhFrontendLocale();

        if (!restBaseUrl || !accessToken) {
            renderContentLoadError();
            return;
        }

        const request = jQuery.ajax({
            type: 'GET',
            url: restBaseUrl + '/content/' + encodeURIComponent(content_id) + '/html',
            data: frontendLocale ? {frontend_locale: frontendLocale} : {},
            dataType: 'json',
            headers: {
                Authorization: 'Bearer ' + accessToken,
                ...(frontendLocale ? {'X-Frontend-Locale': frontendLocale} : {}),
            },
            success: function (response) {
                if (response?.success && renderHtml(response.html)) {
                    return;
                }
                renderContentLoadError();
            },
            error: function (_xhr, status) {
                if (status === 'abort') {
                    return;
                }
                renderContentLoadError();
            },
            complete: function () {
                khActiveRestAjaxRequests.delete(request);
            },
        });
        khActiveRestAjaxRequests.add(request);
    }
    executeWhenReady(doAjax);
}
window.getContent_ContentById = getContent_ContentById;
